import { Injectable, TemplateRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { SiToastService, SiToastTypes } from '@simpl/siemens-brand-ng/toast';
import { environment } from '../../environments/environment';
import { ErrorToastComponent } from '../subcomponents/error-toast/error-toast.component';
import { BehaviorSubject, Subscription, take } from 'rxjs';
import {
  returnAuthDone,
  returnDefaultErrorToastDuration,
  returnIsProduction,
  returnLinkedErrorToastDuration,
  returnPollingTimeOut,
  returnPollingTimeOutDuration,
  returnText
} from '../../state/selectors';
import {
  setAuthDone,
  setDeactivate,
  setDynamicError,
  setLanguage,
  setLanguageIcon,
  setOrderState,
  setPageExpired,
  setPollingTimeOut,
  setToken,
  setTokenRequest
} from '../../state/actions';
import axios from 'axios';
import { Router } from '@angular/router';

export enum ToastType {
  SUCCESS,
  INFO,
  WARNING,
  ERROR
}

export enum OrderState {
  ORDER,
  ACTIVATE,
  CANCEL,
  PROCESSING,
  RENEW,
  ERROR
}

@Injectable({
  providedIn: 'root'
})
export class HelperFunctionsService {
  private toastTemplate!: TemplateRef<any>;
  defaultErrorToastDuration!: number;
  linkedErrorToastDuration!: number;
  returnIsProduction!: boolean;

  // Subject to pass msgKey to ErrorToastComponent
  private errorToastMsgKey$ = new BehaviorSubject<string>(
    'Token_Process_Error'
  );

  constructor(private store: Store) {
    this.store.select(returnDefaultErrorToastDuration).subscribe(duration => {
      this.defaultErrorToastDuration = duration;
    });
    this.store.select(returnLinkedErrorToastDuration).subscribe(duration => {
      this.linkedErrorToastDuration = duration;
    });
    this.store.select(returnIsProduction).pipe(take(1)).subscribe(val => {
      this.returnIsProduction = val;
    });
  }


  /**
   * Set a generic toast template that can handle dynamic translations
   * Used for SUCCESS, INFO, and WARNING toast types
   * @param template - Template reference for toast messages
   */
  setToastTemplate(template: TemplateRef<any>): void {
    this.toastTemplate = template;
  }

  /**
   * Get the current error toast message key
   * Used by ErrorToastComponent to fetch the correct translation
   */
  getErrorToastMsgKey$() {
    return this.errorToastMsgKey$.asObservable();
  }

  /**
   * Extract a user-safe error message from axios/backend error payloads.
   * Prefers backend response body messages over generic axios wrapper text.
   */
  extractErrorMessage(error: any, fallbackMessage = 'Unknown error occurred'): string {
    const responseMessage = this.extractMessageFromValue(error?.response?.data);
    if (responseMessage) {
      return responseMessage;
    }

    const errorMessage = this.extractMessageFromValue(error?.message);
    if (errorMessage) {
      return errorMessage;
    }

    return fallbackMessage;
  }

  private extractMessageFromValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return '';
      }

      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          const parsedMessage = this.extractMessageFromValue(parsed);
          if (parsedMessage) {
            return parsedMessage;
          }
        } catch {
          // Keep original string if it is not valid JSON.
        }
      }

      // Keep only the first line to avoid surfacing stack traces in UI.
      const firstLine = trimmed.split('\n')[0].replace(/^AxiosError:\s*/i, '').trim();
      return firstLine;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const nested = this.extractMessageFromValue(item);
        if (nested) {
          return nested;
        }
      }
      return '';
    }

    if (typeof value === 'object') {
      const keysInPriority = [
        'message',
        'errorDetails',
        'errorMessage',
        'detail',
        'title',
        'error',
        'description'
      ];

      for (const key of keysInPriority) {
        const nested = this.extractMessageFromValue(value[key]);
        if (nested) {
          return nested;
        }
      }
    }

    return '';
  }

  /**
   * The `countdown` function takes a model object with a count and countDownLength property, starts a
   * countdown timer, and executes followupActions when the countdown reaches zero.
   * @param model - The `model` parameter is an object that contains two properties: count and countDownLength
   * @param followupActions - The `followupActions` parameter is a function that will be called once the
   * countdown reaches zero. It is used to specify any actions that should be performed after the
   * countdown is complete.
   */

  countdown(
    model: { count: number; countDownLength: number },
    followupActions: () => void
  ): void {
    model.count = model.countDownLength;
    const timer = setInterval(() => {
      model.count--;
      if (model.count === 0) {
        clearInterval(timer);
        model.count = model.countDownLength;
        followupActions();
      }
    }, 1000);
  }

  /**
   * The `Toasts` function displays toast messages with dynamic translation support.
   * When a toast template is available, messages will update reactively when language changes.
   * @param toastService - The toast service instance
   * @param type - The type of toast message
   * @param msgKey - The translation key for the message
   * @param timeout - Optional duration for the toast
   */
  Toasts(
    toastService: SiToastService,
    type: ToastType,
    msgKey: string,
    timeout?: number
  ): void {
    // Get toast type
    let toastType: SiToastTypes;
    switch (type) {
      case ToastType.SUCCESS:
        toastType = SiToastTypes.SUCCESS;
        break;
      case ToastType.INFO:
        toastType = SiToastTypes.INFO;
        break;
      case ToastType.WARNING:
        toastType = SiToastTypes.WARNING;
        break;
      case ToastType.ERROR:
        toastType = SiToastTypes.DANGER;
        break;
      default:
        toastType = SiToastTypes.INFO;
        break;
    }

    // Create a reactive text fetch function that can be called by Angular's change detection
    const store = this.store;
    const reactiveTextFetch = (id: string) => {
      let msg = '';
      try {
        store.select(returnText(id)).subscribe(val => {
          msg = val || ''; // Ensure we have a string
          // Log the raw value from the selector for debugging
          if (!returnIsProduction) {
            console.log(
              `[Toasts] Raw value from selector for "${id}":`,
              val, ` (type: ${typeof val})`
            );
          }
        });
      } catch (error) {
        // Log any errors that occur during text fetching
        if (!returnIsProduction) {
          console.error(
            `[Toasts] Error fetching translation for "${id}":`,
            error
          );
        }
      }
      // Log the final message that will be used in the toast for debugging
      if (!returnIsProduction) {
        console.log(
          `[Toasts] Fetching translation for key: "${id}", result: "${msg}"`
        );
      }
      // Return the final message
      return msg;
    };

    // Special handling for ERROR type - use ErrorToastComponent with msgKey
    if (type === ToastType.ERROR) {
      const duration = timeout ?? this.linkedErrorToastDuration;
      if (!returnIsProduction) {
        console.log(
          `[Toasts] Showing ERROR toast with msgKey: "${msgKey}", duration: ${duration}`
        );
      }

      try {
        // Set the msgKey before creating the toast so ErrorToastComponent can pick it up
        this.errorToastMsgKey$.next(msgKey);

        // Create toast with ErrorToastComponent
        toastService.showToast({
          content: ErrorToastComponent,
          type: SiToastTypes.DANGER,
          timeout: duration
        });

        if (!returnIsProduction) {
          console.log(
            `[Toasts] ERROR toast shown successfully with msgKey: "${msgKey}"`
          );
        }
      } catch (error) {
        if (!returnIsProduction) {
          console.error(`[Toasts] Error showing ERROR toast:`, error);
        }
      }
    } else if (this.toastTemplate) {
      const duration = timeout ?? this.defaultErrorToastDuration;
      if (!returnIsProduction) {
        console.log(
          `[Toasts] Showing ${ToastType[type]} toast with msgKey: "${msgKey}"`
        );
      }
      toastService.showToast({
        content: this.toastTemplate,
        context: {
          msgKey,
          textFetch: reactiveTextFetch
        },
        type: toastType,
        timeout: duration
      });
    }
  }

  /**
   * The `polling` function is an asynchronous function that repeatedly makes token requests at a
   * specified interval, as long as
   *    the user is authenticated,
   *    the maximum date (store.pollingTimeOut) hasn't been exceeded, checked by continuePolling(),
   *    and the document is visible.
   * @param store - The store is an object that holds the application state. It is typically used
   * in a Redux or NgRx application to manage the state of the application and handle actions and
   * reducers.
   * @param duration - The `duration` parameter is the time interval (in milliseconds) between
   * each polling request. It determines how often the polling function will be called.
   * @param toastService - The `toastService` parameter is an instance of a service that
   * is responsible for displaying toast notifications to the user. It is used to show a warning toast
   * when the polling is stopped.
   * @returns Promise
   */
  async polling(
    store: Store,
    duration: number,
    toastService: SiToastService,
    router: Router
  ): Promise<void> {
    let authDone: boolean;
    const subscription: Subscription = store
      .select(returnAuthDone)
      .subscribe(_authDone => {
        authDone = _authDone;
      });
    this.sleep(250).then(async () => {
      if (authDone === true && this.continuePolling(store)) {
        await this.tokenRequests(store, toastService);
        await new Promise(resolve => {
          setTimeout(resolve, duration);
        });
        this.polling(store, duration, toastService, router);
      } else {
        // stops the automatic firing of polling, if authDone is changed, i.e. sign-out
        subscription.unsubscribe();
        store.dispatch(setDeactivate({ value: true })); // sets the page deactivation grey-out
        store.dispatch(setPageExpired({ value: true })); // shows expired toast
        store.dispatch(setAuthDone({ value: false })); // sets the login/logout button, and forces an auth check
      }
    });
  }

  /**
   * GET request to the backend for EXISTING TOKEN REQUESTS
   * changes visibility of components and notification contents
   * @param store
   * @returns Promise
   */
  async tokenRequests(
    store: Store,
    toastService: SiToastService
  ): Promise<any> {
    // check if the backend URL is set, error handling
    if (
      typeof environment.NG_APP_BACKEND != 'string' ||
      environment.NG_APP_BACKEND === ''
    ) {
      this.Toasts(toastService, ToastType.ERROR, 'BackendUrl_Error');
      return;
    }

    await axios({
      method: 'get',
      url: `${environment.NG_APP_BACKEND}/token`,
      withCredentials: true
    })
      .then(res => {
        // Set the tokenRequest and token values
        store.dispatch(setTokenRequest({ value: res.data }));
        // existing requests or tokens, it's not an empty array
        if (res.data.length > 0) {
          store.dispatch(setToken({ value: res.data[0].tokenId }));
        }
        return res.data;
      })
      .then(data => {
        /**
         * set the OrderStates according to the tokenRequest contents
         * Switch statement had issues (with the data.length > 0 condition) ?!
         */
        if (data.length === 0) {
          store.dispatch(setOrderState({ value: OrderState.ORDER }));
        }
        if (data.length > 0 && data[0].errorDetails === null) {
          if (data[0].state === 'WAIT_FOR_ACTIVATION') {
            store.dispatch(setOrderState({ value: OrderState.ACTIVATE }));
          } else if (data[0].state === 'PROCESSED') {
            store.dispatch(setOrderState({ value: OrderState.CANCEL }));
          }
        }
        if (data.length > 0 && data[0].errorDetails != null) {
          if (!returnIsProduction) {
            console.error('Token fetching errors: ', data[0].errorDetails);
          }
          // set dynamic error details in the store for display in the ErrorToastComponent
          this.store.dispatch(setDynamicError({ value: data[0].errorDetails }));
          this.Toasts(toastService, ToastType.ERROR, 'Dynamic_Error');
          store.dispatch(setOrderState({ value: OrderState.ERROR }));
        }
      })
      .catch((error: any) => {
        if (!returnIsProduction) {
          // eslint-disable-next-line
          console.log('Error in token polling: ', JSON.stringify(error));
        }

        if (Object.keys(error).length !== 0) {
          // Generic authentication error handling for 403 Forbidden
          //    user is authenticated but not authorized to use the service
          if (error.response?.status === 403) {
            store.dispatch(setAuthDone({ value: false }));
            store.dispatch(setOrderState({ value: OrderState.ERROR }));
            this.store.dispatch(
              setDynamicError({ value: this.extractErrorMessage(error, (() => {
                let val = '';
                store.select(returnText('Token_Process_Error')).pipe(take(1)).subscribe(text => {
                  val = text;
                });
                return val;
              })()) })
            );
            this.Toasts(toastService, ToastType.ERROR, 'Dynamic_Error');
          }
          // Specific handling for 406 Not Acceptable - token expired,
          // BE invalidates token, FE triggers re-authentication flow
          if (error.response?.status === 406) {
            window.location.reload();
          } else {
            // Always set dynamic error and show toast once
            this.store.dispatch(
              setDynamicError({ value: this.extractErrorMessage(error) })
            );
            this.Toasts(toastService, ToastType.ERROR, 'Dynamic_Error');
            store.dispatch(setOrderState({ value: OrderState.ERROR }));
          }
        }
      });
  }

  /**
   * The function checks if a language preference is stored in the browser's local storage and dispatches
   * actions to set the language and language icon in the store accordingly.
   * @param store - The `store` parameter is an object that represents the Redux store. It is
   * used to dispatch actions and manage the application state.
   */
  languagePreference(store: Store): void {
    if (localStorage.getItem('language') != null) {
      const lang = localStorage.getItem('language');
      // @ts-ignore: null check is done above
      store.dispatch(setLanguage({ setting: lang }));
      // @ts-ignore: null check is done above
      store.dispatch(setLanguageIcon({ value: lang }));
    }
  }

  /**
   * sleeping function to force synchronicity
   * @param milliseconds
   * @returns Promise
   */
  sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

  /**
   * The function `continuePolling` checks if the polling time has expired for a given store.
   * @param store - The `store` parameter is an object that represents the state of the
   * application. It typically contains data and provides methods to update and retrieve the data.
   * @returns a boolean value, which indicates whether the polling has expired or not.
   */
  continuePolling(store: Store): boolean {
    let _continue = false;
    store
      .select(returnPollingTimeOut)
      .subscribe(_time => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        Date.now() < _time ? (_continue = true) : (_continue = false);
      })
      .unsubscribe();
    return _continue;
  }

  /**
   * The function extends the polling timeout for a store by dispatching an action to update the timeout
   * value.
   * @param store - The `store` parameter is an object that represents the Redux store. It is
   * used to dispatch actions and manage the application state.
   */
  extendPollingTimeOut(store: Store): void {
    store
      .select(returnPollingTimeOutDuration)
      .subscribe(_duration => {
        store.dispatch(setPollingTimeOut({ value: _duration + Date.now() }));
      })
      .unsubscribe();
  }
}
